import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { addTeamMember, removeTeamMember } from '../../../store/actions/organization/teamActions';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import InlineAddMembersSection from '../Shared/InlineAddMembersSection';

interface EditTeamModalProps {
  team: any;
  onSave: (updatedTeam: any) => Promise<void>;
  onAfterSave?: () => Promise<void>;
}

export interface EditTeamModalHandle {
  handleSave: () => Promise<void>;
  isSaving: boolean;
}

const EditTeamModal = forwardRef<EditTeamModalHandle, EditTeamModalProps>(
  ({ team, onSave, onAfterSave }, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [formData, setFormData] = useState({
      name: '',
      description: '',
    });
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (team) {
        setFormData({
          name: team.name || '',
          description: team.description || '',
        });
        setSelectedMembers(team.members?.map((m: any) => m.id) || []);
      }
    }, [team]);

    const handleSave = async () => {
      setIsSaving(true);
      try {
        await onSave({
          name: formData.name,
          description: formData.description,
        });

        const originalMembers = team.members?.map((m: any) => m.id) || [];
        const membersToAdd = selectedMembers.filter(id => !originalMembers.includes(id));
        const membersToRemove = originalMembers.filter((id: any) => !selectedMembers.includes(id));

        for (const memberId of membersToAdd) {
          await dispatch(addTeamMember(team.id, memberId, 'member') as any);
        }

        for (const memberId of membersToRemove) {
          await dispatch(removeTeamMember(team.id, memberId) as any);
        }

        if (onAfterSave) {
          await onAfterSave();
        }
      } catch (err) {
        console.error('Error saving team:', err);
        throw err;
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave,
      isSaving,
    }));

    return (
      <div className="space-y-6 p-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('Team Name')}
          </label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('Enter team name')}
            id="team-name"
          />
        </div>

        <Textarea
          id="team-description"
          label={t('Description')}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder={t('Enter team description')}
          rows={5}
        />

        <InlineAddMembersSection
          selectedMemberIds={selectedMembers}
          onMembersChange={setSelectedMembers}
          excludeIds={[]}
          title={t('Team Members')}
        />
      </div>
    );
  }
);

EditTeamModal.displayName = 'EditTeamModal';

export default EditTeamModal;
